import React from "react";

const Rules = () => {
  return (
    <article className="privacy-page rules-page">
      <main className="privacy-page__main">
        <section className="privacy-page__block privacy-page__head">
          <h1 className="privacy-page__block-title">
            TwoTwentyK Rules of the Game
          </h1>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Introduction</h2>
          <p className="privacy-page__block-descr">
            Welcome to TwoTwentyK, the innovative prediction game where
            strategy, expertise, and excitement meet in the ultimate test of a
            player’s abilities. Whether a player is a sports enthusiast, pop
            culture fanatic, or entertainment expert, TwoTwentyK offers endless
            opportunities to turn knowledge into rewards, all while building an
            exciting collection of trading cards.
          </p>
          <p className="privacy-page__block-descr">
            In TwoTwentyK, players collect cards, craft predictions, and test
            their knowledge against real-world events. The journey revolves
            around several key phases, each designed to offer unique challenges
            and opportunities:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Acquisition Phase: Purchase and rip (open) identity card packs and
              prediction card packs. Opening packs allows players to build a
              strong collection of cards, providing the necessary components to
              craft identities needed to make predictions.
            </li>
            <li className="privacy-page__block-list-item">
              Crafting Phase: Craft Identity Cards from the collection of Month,
              Day, Year, and Category cards to represent specific individuals.
              These Identity Cards are then combined with Event, Trigger, and
              Prize Cards to create Prediction Cards.
            </li>
            <li className="privacy-page__block-list-item">
              Event Phase: This is when players watch the events they have made
              their predictions about, adding excitement as they see the results
              unfold.
            </li>
            <li className="privacy-page__block-list-item">
              Resolution Phase: After the game’s administrators and oracles
              resolve the events, players are able to either ‘Claim Prize’ if
              the prediction resolves true or ‘Return Identity’ if the
              prediction resolves false.
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            The objective of TwoTwentyK is simple: open card packs, collect
            component cards, predict events, watch the outcome, and win rewards.
            With a community of like-minded players and an ever-expanding set of
            game collections, the excitement never ends.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Key Components</h2>
          <p className="privacy-page__block-descr">
            TwoTwentyK revolves around several key components that drive the
            gameplay experience. Understanding each of these components is
            crucial for success:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Identity Cards: Identity Cards are the core assets of TwoTwentyK.
              These cards are crafted by combining Month, Day, Year, and
              Category cards, creating a unique identity that can be used for
              predictions. Identity Cards accumulate statistics, such as the
              number of predictions made, correct predictions, and win streaks,
              which may be used in the future to enhance gameplay.
            </li>
            <li className="privacy-page__block-list-item">
              Event Cards: Event Cards define the specific real-world event that
              players are predicting. Each Event Card includes the type of event
              (e.g., sports match, award show) and the specific details of that
              event (e.g., teams playing, date, or award category).
            </li>
            <li className="privacy-page__block-list-item">
              Trigger Cards: Trigger Cards represent the potential outcome of
              the event being predicted. They provide the conditions that
              determine whether a prediction resolves as true or false, such as
              “Team A wins” or “Actor X wins Best Actor Award.” The Trigger
              Cards define the prediction’s success criteria.
            </li>
            <li className="privacy-page__block-list-item">
              Prize Cards: Prize Cards represent the rewards that can be earned
              if a prediction resolves as true. These cards carry a conditional
              value that becomes redeemable once a successful prediction is
              made. Redeemable Prize cards can be exchanged for cash, which can
              be used by players to make additional purchases in-game, or
              withdrawn to the players’ financial institution.
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            Note: Crafting an Identity Card ‘burns’ the component cards used to
            create the Identity. Burned cards are permanently destroyed and
            cannot be recovered by any means.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Crafting Prediction Cards</h2>
          <p className="privacy-page__block-descr">
            Crafting Prediction Cards requires an Identity Card as well as an
            Event Card, a Trigger Card, and a Prize Card. Prediction Cards are
            predictions about real-world events. Here is how to craft a
            Prediction Card:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Slot an Identity or Event Card: Begin by slotting either an
              Identity Card or an Event Card. The order in which these two cards
              are slotted does not matter, but both must be in place to proceed.
            </li>
            <li className="privacy-page__block-list-item">
              Slot the Remaining Card: Once either the Identity Card or Event
              Card is slotted, slot the remaining card of the two.
            </li>
            <li className="privacy-page__block-list-item">
              Add a Trigger Card: Select a Trigger Card that defines the outcome
              condition (e.g., “Team A wins”).
            </li>
            <li className="privacy-page__block-list-item">
              Add a Prize Card: Attach a Prize Card to determine the potential
              reward if the prediction is correct.
            </li>
            <li className="privacy-page__block-list-item">
              Prediction Cards: Prediction Cards are crafted by combining an
              Identity Card with an Event Card, a Trigger Card, and a Prize
              Card. These cards encapsulate a player’s prediction for a specific
              event. Once a prediction is resolved, the Prediction Card is
              burned, while other components may either return to the player or
              become eligible for rewards, depending on the outcome.
            </li>
            <li className="privacy-page__block-list-item">
              Card Packs: Card Packs are the primary way for players to acquire
              the various Component Cards needed for gameplay. Identity
              Collection Packs contain component cards that allow players to
              create Identities, which can then be used with the corresponding
              Prediction Collection cards. Prediction Collection Packs are
              themed to specific subjects, such as sports leagues (e.g., NFL),
              entertainment awards (e.g., Academy Awards), or political events
              (e.g., elections). This allows players to focus on the areas where
              they have the most knowledge and expertise, enhancing their
              strategic approach.
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            Each of these components works together to create a rich and
            engaging gameplay experience, where strategy, expertise, and the
            thrill of prediction come together. Players must thoughtfully craft
            predictions, utilizing their knowledge of real-world events and
            carefully managing their card collections to maximize their chances
            of success.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            Crafting Cards and Crafting Recipes
          </h2>
          <p className="privacy-page__block-descr">
            Crafting is the core mechanic of TwoTwentyK that allows players to
            create the cards they need to play the game. By combining different
            components, players can craft both Identity Cards and Prediction
            Cards. Below is a detailed explanation of the crafting process and
            the importance of crafting recipes.
          </p>
          <p className="privacy-page__block-descr">Crafting Overview</p>
          <p className="privacy-page__block-descr">
            Crafting is a skill-based activity where players combine various
            component cards to create Identity Cards and Prediction Cards.
          </p>
          <p className="privacy-page__block-descr">Crafting Identity Cards</p>
          <p className="privacy-page__block-descr">
            To craft an Identity Card, players can start with any of the four
            component cards: Month, Day, Year, or Category. The crafting process
            is flexible, allowing players to add the remaining components in any
            order until all four are combined to create a complete Identity
            Card.
          </p>
          <p className="privacy-page__block-descr">
            On the left side of the crafting interface, there is an Identity
            Matching Widget that shows players which potential identities their
            slotted cards can form. This widget also displays the other cards
            needed to complete a particular identity, whether the player
            currently owns those cards, and if they do not, whether the missing
            cards are available for purchase in the marketplace. If a missing
            card is available, players can click the ‘Buy in Marketplace’ button
            directly from the crafting UI to purchase it.
          </p>
          <p className="privacy-page__block-descr">
            Here is a step-by-step guide to crafting an Identity Card:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Start with Any Component Card: Players can start by selecting any
              one of the four component cards (Month, Day, Year, or Category).
            </li>
            <li className="privacy-page__block-list-item">
              Add Remaining Cards in Any Order: Continue adding the remaining
              cards in any order. The crafting process is highly customizable,
              enabling players to choose which cards to slot based on their
              collection.
            </li>
            <li className="privacy-page__block-list-item">
              Utilize the Identity Matching Widget: Refer to the Identity
              Matching Widget to understand which identities are possible and
              what is needed to complete them.
            </li>
            <li className="privacy-page__block-list-item">
              Complete the Identity: Once all four component cards are in place,
              select the ’Craft Identity’ button to create an Identity Card that
              can be used for making predictions.
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            After these four components are slotted and the player selects the
            ‘Craft Prediction’ button, a Prediction Card is created. Unlike
            crafting an Identity Card, the component cards used in crafting a
            Prediction Card are not burned. Instead, they are placed inside the
            newly created Prediction Card and held there until the prediction is
            resolved. Once the event outcome is determined, the Prediction Card
            is resolved, and the component cards are either returned to the
            player or burned based on the result.
          </p>
          <p className="privacy-page__block-descr">
            Crafting Recipes & Rarity Levels
          </p>
          <p className="privacy-page__block-descr">
            Crafting recipes govern how cards are combined to achieve different
            rarity levels. These recipes add a strategic layer to crafting, as
            they require players to carefully select which cards to use based on
            their rarity. Below is a comprehensive description of crafting
            recipes and how they relate to rarity:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Basic Rarity: To craft a basic Identity Card, players need any
              four component cards of the standard rarity. No particular rarity
              requirements are placed on the individual components beyond being
              of the same basic level.
            </li>
            <li className="privacy-page__block-list-item">
              Higher Rarity: Crafting a higher rarity Identity Card involves
              more specific requirements. At least three of the four component
              cards must be of the target rarity level or higher. The fourth
              card can be no more than one rarity level below the target rarity.
              Additionally:
              <ul className="privacy-page__block-list">
                <li className="privacy-page__block-list-item">
                  Year Card Requirement: The Year card must always be at or
                  above the target rarity level.
                </li>
                <li className="privacy-page__block-list-item">
                  Rarity Reduction: If the fourth card is two or more rarity
                  levels below the target, the Identity Card’s final rarity will
                  be reduced to one level above the lowest rarity card used.
                </li>
                <li className="privacy-page__block-list-item">
                  Two-Lower Rule: If fewer than three cards meet the target
                  rarity, the final Identity Card will have the same rarity as
                  the lowest rarity{" "}
                </li>
              </ul>
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            Example: If crafting a rarity level 3 Identity Card:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">Day Card: Level 3</li>
            <li className="privacy-page__block-list-item">Month Card: Level 2</li>
            <li className="privacy-page__block-list-item">Year Card: Level 3</li>
            <li className="privacy-page__block-list-item">
              Category Card: Level 3
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            Result: Crafted Identity is Level 3.
          </p>
          <p className="privacy-page__block-descr">
            If the Category Card were Level 1, the crafted Identity would be
            downgraded to Level 2.
          </p>
          <p className="privacy-page__block-descr">
            Example of Two-Lower Rule: Suppose you are crafting an Identity Card
            with the following:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">Day Card: Level 4</li>
            <li className="privacy-page__block-list-item">Month Card: Level 4</li>
            <li className="privacy-page__block-list-item">Year Card: Level 3</li>
            <li className="privacy-page__block-list-item">
              Category Card: Level 1
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            In this scenario, because fewer than three of the component cards
            meet the target rarity level (Level 4), the resulting Identity Card
            will default to the lowest rarity card used, which is Level 1.
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Prediction Card Rarity: The rarity of a Prediction Card is
              predominantly determined by the Identity Card used in its
              crafting. The Event, Trigger, and Prize Cards can only influence
              the overall rarity by downgrading it if they do not meet the
              rarity of the Identity Card. For a Prediction Card to retain the
              same rarity as the Identity Card:
              <ul className="privacy-page__block-list">
                <li className="privacy-page__block-list-item">
                  At least two of the Event, Trigger, and Prize Cards must match
                  the rarity of the Identity Card.
                </li>
                <li className="privacy-page__block-list-item">
                  The fourth card can be one level below but not more.
                </li>
              </ul>
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            Example: Crafting a rarity level 3 Prediction Card:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Identity Card: Level 3
            </li>
            <li className="privacy-page__block-list-item">Event Card: Level 3</li>
            <li className="privacy-page__block-list-item">Trigger Card: Level 2</li>
            <li className="privacy-page__block-list-item">Prize Card: Level 3</li>
          </ul>
          <p className="privacy-page__block-descr">
            Result: Prediction Card is Level 3.
          </p>
          <p className="privacy-page__block-descr">
            However, if the Trigger Card were Level 1, the resulting Prediction
            Card would be downgraded to Level 2.
          </p>
          <p className="privacy-page__block-descr">
            These crafting recipes provide significant depth and complexity,
            requiring players to think strategically about which cards to use
            and how to best achieve their desired outcomes. Dynamic rarity
            calculators are provided on both the Identity and Prediction
            crafting interfaces. The calculators update as cards are added or
            removed from the crafting slots, making it easy for players to see
            what rarity their card combination will be.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Gameplay Phases in Detail</h2>
          <p className="privacy-page__block-descr">
            The gameplay experience in TwoTwentyK revolves around four key
            phases: Acquisition Phase, Crafting Phase, Event Phase, and
            Resolution Phase. Each of these phases offers unique opportunities
            for players to make strategic decisions and progress within the
            game. Below, we break down each phase in detail:
          </p>
        </section>

        <section className="privacy-page__block">
          <h3 className="privacy-page__block-title">Acquisition Phase</h3>
          <p className="privacy-page__block-descr">
            The Acquisition Phase is where players obtain the essential cards
            needed to participate in the game. During this phase, players can
            purchase and open card packs. The card packs come in different
            types: Identity Collection Packs and Prediction Collection Packs.
            These packs contain the component cards needed for crafting Identity
            and Prediction Cards.
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Identity Collection Packs: These packs include cards such as
              Month, Day, Year, and Category that are used to craft Identity
              Cards. Building a diverse set of these cards is crucial for being
              able to craft a variety of identities so players can make all the
              different predictions they desire.
            </li>
            <li className="privacy-page__block-list-item">
              Prediction Collection Packs: These packs contain Event, Trigger,
              and Prize Cards. These cards are required for crafting Prediction
              Cards that will be used to make predictions on real-world events.
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            Players must decide which packs to purchase based on their gameplay
            strategy. Most players will want to purchase enough Identity
            Collection Packs to easily create the identities they need for
            making predictions, while also acquiring Prediction Collection Packs
            aligned with the event types they are interested in. However, there
            are alternative gameplay strategies to consider. For example,
            players who want to craft a wide variety of identities for trading
            purposes may focus primarily on Identity Collection Packs to supply
            the marketplace. Others may specialize in collecting a large number
            of Event Cards from a specific Prediction Collection and make those
            available for sale to players who need a particular event on short
            notice or prefer not to buy multiple packs while searching for a
            single card. This strategic diversity allows players to determine
            their own path based on their goals within the game.
          </p>
        </section>

        <section className="privacy-page__block">
          <h3 className="privacy-page__block-title">Crafting Phase</h3>
          <p className="privacy-page__block-descr">
            The Crafting Phase is where players create Identity Cards and
            Prediction Cards by combining the various component cards they have
            acquired. Crafting is a strategic process that requires players to
            think carefully about how to best use their available cards.
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Crafting Identity Cards: Players use Month, Day, Year, and
              Category cards to create Identity Cards. The Identity Matching
              Widget helps players see which identities they can create with the
              cards they have available. Some cards are easier to obtain than
              others, so players need to decide what identities they want to use
              hard-to-find cards to create.
            </li>
            <li className="privacy-page__block-list-item">
              Crafting Prediction Cards: Players use an Identity Card along with
              an Event Card, a Trigger Card, and a Prize Card to create a
              Prediction Card. The crafted Prediction Card encapsulates the
              player’s prediction for a specific event.
            </li>
          </ul>
        </section>

        <section className="privacy-page__block">
          <h3 className="privacy-page__block-title">Event Phase</h3>
          <p className="privacy-page__block-descr">
            The Event Phase is where the trading card game mechanics of the game
            meet the real world. During this phase, the events that players have
            made predictions about take place in the real world. Players watch
            these events unfold, waiting to see if their predictions come true.
          </p>
        </section>

        <section className="privacy-page__block">
          <h3 className="privacy-page__block-title">Resolution Phase</h3>
          <p className="privacy-page__block-descr">
            The Resolution Phase occurs after the real-world event has concluded
            and the outcomes are known. During this phase, the game’s
            administrators and oracles determine the result of each prediction
            and resolve the Prediction Cards. The source of truth for resolving
            predictions will be identified at the time the card packs for a
            collection are made available for purchase. Events may be resolved
            either manually by a game administrator or programmatically through
            the use of an oracle.
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Claim Prize: If the prediction resolves true, the player can
              select ‘Claim Prize’ to claim the Prize Card. Upon claiming the
              Prize Card, it is flagged as ‘redeemable’, and moved back into the
              players’ inventory. The player can then redeem the Prize Card for
              cash, which is deposited into the player’s in-game wallet, at
              their convenience. The Identity Card used in the prediction is
              returned to the player, allowing it to be reused in future
              predictions. The Event and Trigger Cards are burned, as is the
              now-empty Prediction Card.
            </li>
            <li className="privacy-page__block-list-item">
              Return Identity: If the prediction resolved false, the player may
              select ‘Return Identity’, which will cause the Identity Card used
              in the prediction to be moved back into the player’s inventory.
              The Event, Trigger, and Prize Cards used in the prediction are
              burned along with the now-empty Prediction Card.
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            The Resolution Phase concludes the gameplay loop.
          </p>
        </section>
      </main>
    </article>
  );
};

export default Rules;
